import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { formatCompactINR, formatINR } from "@/lib/format";

const CHART_MARGIN = { top: 10, right: 10, bottom: 0, left: 0 };
const GRID_STROKE = "rgba(255,255,255,0.05)";
const AXIS_STROKE = "rgba(255,255,255,0.08)";
const AXIS_LINE = { stroke: AXIS_STROKE };
const TICK_STYLE = { fill: "#94A3B8", fontSize: 12 };
const TICK_STYLE_SM = { fill: "#94A3B8", fontSize: 11 };
const TOOLTIP_LABELS = {
  corpusNominal: "Corpus value (nominal)",
  corpusReal: "Corpus value (today's \u20B9)",
  annualWithdrawal: "Annual income drawn",
};
const yTickFormatter = (v) => formatCompactINR(v);

function TipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[#0A0F1C]/95 backdrop-blur px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-400 mb-1">Age {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 num-font">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: p.color }}
          />
          <span className="text-slate-300">{TOOLTIP_LABELS[p.dataKey] || p.dataKey}:</span>
          <span className="text-white font-medium">{formatCompactINR(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function DistributionChart({ plan }) {
  const data = plan.distributionPeriod;
  const lastAge = data.length ? data[data.length - 1].age : plan.inputs.freedomAge;
  const finalCorpus = data.length ? data[data.length - 1].corpusNominal : 0;
  const totalWithdrawn = data.reduce((s, d) => s + d.annualWithdrawal, 0);

  return (
    <section
      className="mt-10 rounded-2xl border border-white/10 bg-[#111827]/70 p-6 sm:p-8"
      data-testid="distribution-chart-section"
    >
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
            Distribution Period
          </div>
          <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
            Life after freedom
          </h3>
          <p className="mt-2 text-sm text-slate-400 max-w-xl">
            How your corpus grows with inflation even as you keep withdrawing a rising income
            every year — until age 80.
          </p>
        </div>
        <div className="flex items-center gap-5 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#F97316]/70" />
            Annual withdrawal
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-emerald-400" />
            Corpus (nominal)
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-slate-400/70 border border-dashed" />
            Corpus (today's ₹)
          </div>
        </div>
      </div>

      <div className="h-[320px] sm:h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id="grad-withdraw" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F97316" stopOpacity={0.85} />
                <stop offset="100%" stopColor="#F97316" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={GRID_STROKE} strokeDasharray="3 3" />
            <XAxis
              dataKey="age"
              stroke="#64748B"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={AXIS_LINE}
            />
            <YAxis
              yAxisId="left"
              stroke="#64748B"
              tick={TICK_STYLE}
              tickFormatter={yTickFormatter}
              tickLine={false}
              axisLine={AXIS_LINE}
              width={80}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#64748B"
              tick={TICK_STYLE_SM}
              tickFormatter={yTickFormatter}
              tickLine={false}
              axisLine={AXIS_LINE}
              width={70}
            />
            <Tooltip content={<TipContent />} />
            <Bar
              yAxisId="right"
              dataKey="annualWithdrawal"
              fill="url(#grad-withdraw)"
              radius={[4, 4, 0, 0]}
              barSize={14}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="corpusNominal"
              stroke="#34D399"
              strokeWidth={2.5}
              dot={false}
              isAnimationActive
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="corpusReal"
              stroke="#94A3B8"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              isAnimationActive
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Final corpus at {lastAge}
          </div>
          <div className="mt-1 font-heading text-xl font-semibold num-font text-emerald-400">
            {formatCompactINR(finalCorpus)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 num-font">
            = {formatINR(finalCorpus)}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Total withdrawn by {lastAge}
          </div>
          <div className="mt-1 font-heading text-xl font-semibold num-font text-[#F97316]">
            {formatCompactINR(totalWithdrawn)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Across {data.length} years of freedom
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Real value maintained
          </div>
          <div className="mt-1 font-heading text-xl font-semibold num-font text-slate-200">
            {formatCompactINR(plan.targetCorpus)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Corpus keeps full purchasing power
          </div>
        </div>
      </div>
    </section>
  );
}
